import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { Conversation } from '../types/Conversation';
import { filterMessagesForCommentVisibility } from '../utils/instagramComposerUtils';

interface UseConversationMessagePaneOptions {
  selectedId: string | null;
  selectedConv: Conversation | null;
  detailsLoading: boolean;
  showCommentThreads: boolean;
}

interface UseConversationMessagePaneResult {
  messagesPaneRef: RefObject<HTMLDivElement | null>;
  unreadCount: number;
  scrollToBottom: () => void;
}

export const useConversationMessagePane = ({
  selectedId,
  selectedConv,
  detailsLoading,
  showCommentThreads,
}: UseConversationMessagePaneOptions): UseConversationMessagePaneResult => {
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesPaneRef = useRef<HTMLDivElement>(null);
  const lastReadMessageCount = useRef(0);
  const lastFirstMessageId = useRef<string | null>(null);
  const lastLastMessageId = useRef<string | null>(null);
  const initialScrollConversationId = useRef<string | null>(null);

  const visibleMessages = useMemo(() => (
    selectedConv
      ? filterMessagesForCommentVisibility(selectedConv.messages, showCommentThreads)
      : []
  ), [selectedConv, showCommentThreads]);

  const messageCount = visibleMessages.length;
  const firstMessageId = visibleMessages[0]?.id || null;
  const lastMessageId = messageCount > 0
    ? visibleMessages[messageCount - 1].id
    : null;

  useEffect(() => {
    setUnreadCount(0);
    lastReadMessageCount.current = messageCount;
    lastFirstMessageId.current = firstMessageId;
    lastLastMessageId.current = lastMessageId;
    initialScrollConversationId.current = null;
  }, [firstMessageId, lastMessageId, messageCount, selectedId, showCommentThreads]);

  useEffect(() => {
    if (!selectedConv || !messagesPaneRef.current) return;

    const newMessageCount = messageCount;
    const nextFirstMessageId = firstMessageId;
    const nextLastMessageId = lastMessageId;

    const olderMessagesPrepended = newMessageCount > lastReadMessageCount.current
      && lastReadMessageCount.current > 0
      && nextFirstMessageId !== lastFirstMessageId.current
      && nextLastMessageId === lastLastMessageId.current;

    if (olderMessagesPrepended) {
      lastReadMessageCount.current = newMessageCount;
      lastFirstMessageId.current = nextFirstMessageId;
      lastLastMessageId.current = nextLastMessageId;
      return;
    }

    if (newMessageCount > lastReadMessageCount.current) {
      const pane = messagesPaneRef.current;
      const isAtBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight < 50;

      if (isAtBottom) {
        setTimeout(() => {
          pane.scrollTop = pane.scrollHeight;
        }, 100);
        lastReadMessageCount.current = newMessageCount;
        return;
      }

      setUnreadCount(current => current + (newMessageCount - lastReadMessageCount.current));
      lastReadMessageCount.current = newMessageCount;
    } else if (newMessageCount < lastReadMessageCount.current) {
      lastReadMessageCount.current = newMessageCount;
      setUnreadCount(0);
    }

    lastFirstMessageId.current = nextFirstMessageId;
    lastLastMessageId.current = nextLastMessageId;
  }, [firstMessageId, lastMessageId, messageCount, selectedConv]);

  const scrollToBottom = useCallback(() => {
    if (messagesPaneRef.current) {
      messagesPaneRef.current.scrollTop = messagesPaneRef.current.scrollHeight;
      setUnreadCount(0);
      lastReadMessageCount.current = messageCount;
    }
  }, [messageCount]);

  useEffect(() => {
    if (!detailsLoading && selectedConv && messagesPaneRef.current && initialScrollConversationId.current !== selectedConv.id) {
      const timer = setTimeout(() => {
        scrollToBottom();
        initialScrollConversationId.current = selectedConv.id;
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [detailsLoading, scrollToBottom, selectedConv]);

  return {
    messagesPaneRef,
    unreadCount,
    scrollToBottom,
  };
};
